import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  RewardAttributes,
  createReward as createRewardUtil,
} from '@loyalty/shared-common/rewards-api-client';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { extractErrorMessage, uuid } from '@loyalty/shared-common';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';
import { useRules } from '../rules/RulesContext';

interface RewardsContextInterface {
  rewards: RewardAttributes[];
  selectedReward: RewardAttributes | undefined;
  createReward: (reward?: RewardAttributes) => void;
  duplicate: (reward: RewardAttributes) => void;
  addReward: (reward: RewardAttributes, isNew?: boolean) => void;
  updateReward: (rewardId: string, reward: RewardAttributes) => void;
  refreshReward: (id: string) => Promise<void>;
  replaceReward: (reward: RewardAttributes) => void;
  archiveReward: (rewardId: string) => void;
  restoreReward: (rewardId: string) => void;
  deleteUnsavedReward: (rewardId: string) => void;
  deleteUnsavedRewards: () => void;
  fetchRewards: () => void;
  setSelectedReward: (reward: RewardAttributes | undefined) => void;
  isLoading: IsLoading;
}

export const RewardsContext = createContext<RewardsContextInterface>({} as any);

export function useRewards() {
  return useContext(RewardsContext);
}

export function RewardsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { rewardsApi, managerApi } = useApi();
  const { addLoader, removeLoader, isLoading } = useLoaders();
  const [rewards, setRewards] = useState<RewardAttributes[]>([]);
  const [selectedReward, setSelectedReward] = useState<RewardAttributes | undefined>();
  const { fetchRules } = useRules();

  const createReward = useCallback(() => {
    const newReward = {
      ...createRewardUtil(program.accountId, program),
      isNew: true,
    };
    setRewards(prev => [newReward as RewardAttributes, ...prev]);
    setSelectedReward(newReward as RewardAttributes);
  }, [program]);

  const addReward = useCallback(
    async (rewardPayload: RewardAttributes, isNew?: boolean) => {
      addLoader('addReward');
      rewardsApi
        .addReward(program.id, rewardPayload)
        .then(newReward => {
          const rewardWithStats = { promocodeStats: newReward.promocodeStats, ...newReward };

          if (isNew) {
            setRewards(prev => [newReward, ...prev]);
          } else {
            setRewards(prev =>
              prev.map(reward => (reward.id === rewardPayload.id ? rewardWithStats : reward)),
            );
          }

          setSelectedReward(newReward);
          notify(t('shared.saved'), 'success');
        })
        .catch(error => notify(t('rewards.errors.failed-save-rules', { error }), 'error'))
        .finally(() => removeLoader('addReward'));
    },
    [addLoader, rewardsApi, program, removeLoader, notify, t],
  );

  const duplicate = useCallback(
    async (reward: RewardAttributes) => {
      const newReward: RewardAttributes = {
        id: uuid(), // required for sidebar selection to work
        accountId: reward.accountId,
        programId: reward.programId,
        i18nAttributes: Object.fromEntries(
          Object.entries(reward.i18nAttributes).map(([key, value]) => [
            key,
            { ...value, name: incrementName(value.name) },
          ]),
        ),
        isNew: true,
        data: reward.data,
        clientIds: reward.clientIds,
      } as any;
      setRewards(prev => [...prev, newReward]);
    },
    [addReward],
  );

  const updateReward = useCallback(
    async (rewardId: string, reward: RewardAttributes) => {
      addLoader('updateReward');

      rewardsApi
        .updateReward(rewardId, reward)
        .then(updatedReward => {
          setRewards(prev =>
            prev.map(reward => (reward.id === updatedReward.id ? updatedReward : reward)),
          );
          if (selectedReward?.id === updatedReward.id) {
            setSelectedReward(updatedReward);
          }
          notify(t('shared.saved'), 'success');
        })
        .catch(error =>
          notify(
            t('rewards.errors.failed-save-rules', { error: extractErrorMessage(error) }),
            'error',
          ),
        )
        .finally(() => removeLoader('updateReward'));
    },
    [addLoader, rewardsApi, removeLoader, notify, t, selectedReward],
  );

  const refreshReward = useCallback(
    async (id: string) => {
      addLoader('refreshReward');
      rewardsApi
        .getReward(id)
        .then(reward => {
          setRewards(prev => prev.map(r => (r.id === reward.id ? reward : r)));
          if (selectedReward?.id === reward.id) {
            setSelectedReward(reward);
          }
        })
        .finally(() => removeLoader('refreshReward'));
    },
    [addLoader, rewardsApi, removeLoader, selectedReward],
  );

  const fetchRewards = useCallback(async () => {
    addLoader('fetchRewards');

    rewardsApi
      .getRewards(program.id)
      .then(setRewards)
      .catch(error => {
        console.error('Could not fetchRewards', error);
        setRewards([]);
      })
      .finally(() => removeLoader('fetchRewards'));
  }, [addLoader, rewardsApi, removeLoader, program]);

  const archiveReward = useCallback(
    async (rewardId: string) => {
      addLoader('archiveReward');

      await managerApi
        .archiveReward(rewardId)
        .then(archivedReward => {
          const newRewards = rewards.map(reward =>
            reward.id === rewardId ? archivedReward : reward,
          );

          setRewards(newRewards);

          return fetchRules(RuleType.BURN);
        })
        .finally(() => removeLoader('archiveReward'));
    },
    [addLoader, managerApi, removeLoader, rewards, fetchRules],
  );

  const restoreReward = useCallback(
    async (rewardId: string) => {
      addLoader('restoreReward');

      await rewardsApi
        .restoreReward(rewardId)
        .then(restoredReward => {
          setRewards(prev =>
            prev.map(reward => (reward.id === rewardId ? restoredReward : reward)),
          );

          setSelectedReward(restoredReward);
        })
        .finally(() => removeLoader('restoreReward'));
    },
    [addLoader, managerApi, removeLoader],
  );

  const deleteUnsavedReward = useCallback((rewardId: string) => {
    setSelectedReward(undefined);
    setRewards(rewards => rewards.filter(reward => reward.id !== rewardId));
  }, []);

  const deleteUnsavedRewards = useCallback(() => {
    setSelectedReward(undefined);
    setRewards(rewards => rewards.filter(reward => !reward.isNew));
  }, []);

  const replaceReward = useCallback(
    (reward: RewardAttributes) => {
      setRewards(prev => prev.map(r => (r.id === reward.id ? reward : r)));
      if (reward.id === selectedReward?.id) {
        setSelectedReward(reward);
      }
    },
    [selectedReward],
  );

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  const context = {
    rewards,
    selectedReward,
    createReward,
    addReward,
    updateReward,
    refreshReward,
    replaceReward,
    fetchRewards,
    archiveReward,
    duplicate,
    restoreReward,
    deleteUnsavedReward,
    deleteUnsavedRewards,
    setSelectedReward,
    isLoading,
  };

  return <RewardsContext.Provider value={context}>{children}</RewardsContext.Provider>;
}

function incrementName(name: string): string {
  const regExp = / (\d+)$/;
  const match = name.match(regExp);
  if (!match) {
    return `${name} 1`;
  }
  const number = parseInt(match[1], 10);
  return name.replace(regExp, ` ${number + 1}`);
}
